import React, { useState, useEffect } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Box from '@amzn/meridian/box';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import Icon from '@amzn/meridian/icon';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import {
  setIsTaskGroupModalOpen,
  setSelectedTaskGroupTableRow,
  setTaskGroupTableSettings,
  TaskGroupTableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

// **** Types **** //

interface SortProps {
  sortColumn: keyof TaskGroupTableDataRow;
  sortDirection: TableSortDirection;
}

// **** Components **** //

/**
 * Default component
 */
const TaskGroupTable = () => {
  const dispatch = useAppDispatch();
  const [sortColumn, setSortColumn] = useState<keyof TaskGroupTableDataRow>('label');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const { taskGroupTableData, taskGroupSearchFieldValue, taskGroupTableSettings } = useAppSelector(
    (state) => state.taskConfigPage,
  );
  const [sortedTableData, setSortedTableData] = useState(taskGroupTableData);
  const { currentPage, perPage } = taskGroupTableSettings;

  // Filter, sorting, and pagination
  useEffect(() => {
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      taskGroupTableData,
      taskGroupSearchFieldValue,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setSortedTableData(paginatedData);
    dispatch(setTaskGroupTableSettings({ totalPages }));
  }, [sortColumn, sortDirection, taskGroupTableData, taskGroupSearchFieldValue, currentPage, perPage]);

  // Go back to first page when filter/sort
  useEffect(() => {
    dispatch(setTaskGroupTableSettings({ currentPage: 1 }));
  }, [taskGroupSearchFieldValue, sortColumn, sortDirection]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="view-and-edit-tasks__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={(props: SortProps) => {
          setSortDirection(props.sortDirection);
          setSortColumn(props.sortColumn);
        }}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        {/* Header */}
        <TableRow>
          <TableCell width="20%" sortColumn="label">
            Group Name
          </TableCell>
          <TableCell sortColumn="region">State/Region/Province</TableCell>
          <TableCell sortColumn="country">Country</TableCell>
          <TableCell sortColumn="identifyBodyLocation">Identify Body Location?</TableCell>
          <TableCell sortColumn="isMeasuredBy">Is Measured By?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>

        {/* Rows */}
        {sortedTableData.map((taskGroup) => (
          <UncontrolledExpandableRow key={taskGroup.id} highlightOnHover={true} data-testid="task-group-table__row">
            <TableCell>{taskGroup.label}</TableCell>
            <TableCell>{taskGroup.region}</TableCell>
            <TableCell>{taskGroup.country}</TableCell>
            <TableCell>{taskGroup.identifyBodyLocation ? 'Yes' : 'No'}</TableCell>
            <TableCell>{taskGroup.isMeasuredBy}</TableCell>
            <TableCell>{''}</TableCell>
            <TableCell>
              <div
                data-testid="task-group-table__row__open-edit-modal-button"
                className="view-and-edit-tasks__edit-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setIsTaskGroupModalOpen(true));
                  dispatch(setSelectedTaskGroupTableRow(taskGroup));
                }}
              >
                <Icon tokens={editTokens}></Icon>
              </div>
            </TableCell>
            {taskGroup.tasks?.map((task) => (
              <TableRow key={task.id} backgroundColor="secondary">
                <TableCell>{task.label}</TableCell>
                <TableCell>{taskGroup.region}</TableCell>
                <TableCell>{taskGroup.country}</TableCell>
                <TableCell>{task.identifyBodyLocation ? 'Yes' : 'No'}</TableCell>
                <TableCell>{task.isMeasuredBy}</TableCell>
                <TableCell>{''}</TableCell>
                <TableCell>{''}</TableCell>
              </TableRow>
            ))}
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

export default TaskGroupTable;
